import {
  ComputingObject,
  Objects,
  getObject,
  ObjectType,
} from "../../../src/utils/shop";
import {
  RegisteredObject,
  ObjectConfiguration,
} from "../types/companion";
import { generateObjectId, storage } from "./localStorage";

export { Objects, ObjectType } from "../../../src/utils/shop";

export interface ObjectRegistrationData {
  objectId: string;
  nickname?: string;
  label?: string;
  setupData?: Partial<ObjectConfiguration>;
  tagId?: string; // NFC tag identifier (optional for some objects like Phone Pillow on iOS)
}

class CompanionObjectManager {
  // Get all available objects from the main app
  getAvailableObjects(): ComputingObject[] {
    return Objects.filter((obj) => obj.live); // Only show live objects for now
  }

  // Get a specific available object
  getAvailableObject(objectId: string): ComputingObject | null {
    return getObject(objectId);
  }

  // Get objects available for registration (not already registered)
  getUnregisteredObjects(
    registeredObjects: RegisteredObject[]
  ): ComputingObject[] {
    const registeredObjectIds = registeredObjects.map((obj) => obj.objectId);
    return this.getAvailableObjects().filter(
      (obj) => !registeredObjectIds.includes(obj.id)
    );
  }

  // Create default configuration for an object type
  createDefaultConfiguration(objectType: ObjectType): ObjectConfiguration {
    switch (objectType) {
      case ObjectType.Frame:
        // Check if this is a fortune object based on available objects
        const allObjects = this.getAvailableObjects();
        const fortuneObjects = allObjects.filter(
          (obj) =>
            obj.type === ObjectType.Frame && obj.id === "fortune-webstones"
        );

        if (fortuneObjects.length > 0) {
          return {
            type: objectType,
            fortune: {
              enableDefaultMessages: true,
              customMessages: [],
            },
          };
        }
        break;

      case ObjectType.Image:
        // For image objects, minimal config for now
        return {
          type: objectType,
        };

      default:
        return {
          type: objectType,
        };
    }

    return { type: objectType };
  }

  // Register a new object with setup data
  registerObject(data: ObjectRegistrationData): RegisteredObject | null {
    const availableObject = this.getAvailableObject(data.objectId);
    if (!availableObject) {
      console.error("Attempted to register unknown object:", data.objectId);
      return null;
    }

    const registeredObject: RegisteredObject = {
      id: generateObjectId(),
      objectId: data.objectId,
      registeredAt: new Date().toISOString(),
      nickname: data.nickname,
      label: data.label,
      configuration: data.setupData
        ? { ...this.createDefaultConfiguration(availableObject.type), ...data.setupData }
        : this.createDefaultConfiguration(availableObject.type),
      tagId: data.tagId,
    };

    const success = storage.addRegisteredObject(registeredObject);
    return success ? registeredObject : null;
  }

  // Update an existing registered object
  updateRegisteredObject(
    id: string,
    updates: Partial<Pick<RegisteredObject, "nickname" | "configuration">>
  ): boolean {
    return storage.updateRegisteredObject(id, updates);
  }

  // Remove a registered object
  unregisterObject(id: string): boolean {
    return storage.removeRegisteredObject(id);
  }

  // Get registered object with its full definition
  getRegisteredObjectWithDefinition(registeredObject: RegisteredObject): {
    registered: RegisteredObject;
    definition: ComputingObject;
  } | null {
    const definition = this.getAvailableObject(registeredObject.objectId);
    if (!definition) return null;

    return {
      registered: registeredObject,
      definition,
    };
  }

  // Validation helpers
  isValidObjectId(objectId: string): boolean {
    return this.getAvailableObject(objectId) !== null;
  }

  isObjectAlreadyRegistered(
    objectId: string,
    registeredObjects: RegisteredObject[]
  ): boolean {
    return registeredObjects.some((obj) => obj.objectId === objectId);
  }

  // Platform detection utilities
  getPlatformCapabilities() {
    const userAgent = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/.test(userAgent);

    return {
      hasWebNFC: "NDEFReader" in window && isAndroid, // Web NFC only works on Android
      isIOS,
      isAndroid,
      canInstall:
        "serviceWorker" in navigator && "BeforeInstallPromptEvent" in window,
    };
  }

  // URL parsing for object registration
  parseObjectUrlForId(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split("/");

      // Look for object ID in various URL patterns
      // Pattern 1: /object/id
      if (pathParts.length >= 3 && pathParts[1] === "object") {
        return pathParts[2];
      }

      // Pattern 2: Query parameter
      const idParam = urlObj.searchParams.get("id");
      if (idParam) return idParam;

      // Pattern 3: Fragment
      const fragment = urlObj.hash.replace("#", "");
      if (fragment && this.isValidObjectId(fragment)) {
        return fragment;
      }

      return null;
    } catch (error) {
      console.warn("Failed to parse object URL:", error);
      return null;
    }
  }

  // Generate object URL for sharing/identification
  generateObjectUrl(objectId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/object/${objectId}`;
  }
}

// Export singleton instance
export const objectManager = new CompanionObjectManager();

// Convenience functions
export const getAvailableObjects = () => objectManager.getAvailableObjects();
export const getAvailableObject = (id: string) =>
  objectManager.getAvailableObject(id);
export const registerObject = (data: ObjectRegistrationData) =>
  objectManager.registerObject(data);
export const updateRegisteredObject = (
  id: string,
  updates: Partial<Pick<RegisteredObject, "nickname" | "configuration">>
) => objectManager.updateRegisteredObject(id, updates);
export const unregisterObject = (id: string) =>
  objectManager.unregisterObject(id);
export const getPlatformCapabilities = () =>
  objectManager.getPlatformCapabilities();
